import { QueryClient } from '@tanstack/react-query';
import { AuthError, RateLimitError } from '../api/errors';

/**
 * Shared React Query configuration. Auth errors are never retried (a 401 means
 * refresh already failed; a 403 won't change on retry); everything else gets a
 * bounded exponential backoff.
 *
 * RateLimitError (the gateway's real fixed-window 60 req/min-per-IP limiter — same
 * one every other client in this codebase retries around: BillingService's
 * IntegrationTests Api.cs helper, scripts/test-crud.sh, e2e/utils/api.ts's
 * withRetry) gets a longer ceiling than other errors: 3 attempts' ~7s of backoff
 * was observed to not reliably outlast the window under real concurrent load
 * (product search silently stuck loading past a 15s UI assertion), so it gets up
 * to 6 attempts and a 20s cap instead of 8s.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof AuthError) return false;
          if (error instanceof RateLimitError) return failureCount < 6;
          return failureCount < 3;
        },
        retryDelay: (attempt, error) =>
          error instanceof RateLimitError
            ? Math.min(1000 * 2 ** attempt, 20_000)
            : Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: { retry: false },
    },
  });
}

/** Query key factory — one namespace per domain so invalidation is surgical. */
export const queryKeys = {
  catalog: ['catalog'] as const,
  bookings: (customerId?: string) => ['bookings', customerId ?? 'all'] as const,
  booking: (id: string) => ['booking', id] as const,
  bookingHistory: (id: string, cursor?: string) => ['booking', id, 'history', cursor ?? 'first'] as const,
  schedule: (date: string) => ['schedule', date] as const,
  customerJobs: (customerId: string) => ['schedule', 'customer', customerId] as const,
  workers: ['workers'] as const,
  nearbyWorkers: (lat: number, lng: number, r: number) => ['workers', 'nearby', lat, lng, r] as const,
  shifts: (workerId: string) => ['shifts', workerId] as const,
  workerMe: ['workers', 'me'] as const,
  workerDashboard: ['workers', 'me', 'dashboard'] as const,
  customers: ['customers'] as const,
  customer360: (id: string) => ['customer360', id] as const,
  cmsPublic: (page: string) => ['cms', 'public', page] as const,
  cmsLayout: (page: string) => ['cms', 'layout', page] as const,
  cmsSections: (page?: string) => ['cms', 'sections', page ?? 'all'] as const,
  invoices: (customerId?: string) => ['invoices', customerId ?? 'all'] as const,
  accounting: (from: string, to: string) => ['accounting', from, to] as const,
  analytics: (kind: string, from: string, to: string) => ['analytics', kind, from, to] as const,
  orderAnalyticsSummary: (days: number) => ['order-analytics', 'summary', days] as const,
  orderTopProducts: (limit: number, days: number) => ['order-analytics', 'top-products', limit, days] as const,
  orderAnalyticsFunnel: (days: number) => ['order-analytics', 'funnel', days] as const,
  orderAnalyticsCohorts: (months: number) => ['order-analytics', 'cohorts', months] as const,
  orderAnalyticsOverview: () => ['order-analytics', 'overview'] as const,
  notificationTemplates: ['notification-templates'] as const,
  notificationTemplate: (key: string) => ['notification-templates', key] as const,
  notificationsAdmin: (status?: string, channel?: string, cursor?: string) =>
    ['notifications-admin', status ?? 'all', channel ?? 'all', cursor ?? 'first'] as const,
  users: (role?: string) => ['users', role ?? 'all'] as const,
  campaigns: ['campaigns'] as const,
  testimonials: ['testimonials'] as const,
  testimonialsPublic: (featured?: boolean) => ['testimonials', 'public', featured ?? 'all'] as const,
  subscriptionPlans: (includeInactive: boolean) => ['subscriptionPlans', includeInactive] as const,
  tenantSubscription: ['tenantSubscription'] as const,
  allTenantSubscriptions: ['allTenantSubscriptions'] as const,
  subscriptionDashboard: ['subscriptionDashboard'] as const,
  subscriptionMrrTrend: ['subscriptionMrrTrend'] as const,
  subscriptionChurnTrend: ['subscriptionChurnTrend'] as const,
  subscriptionPlanDistribution: ['subscriptionPlanDistribution'] as const,
  staffUserCount: ['staffUserCount'] as const,
  tenants: ['tenants'] as const,
  tenant: (id: string) => ['tenants', id] as const,
  blog: ['blog'] as const,
  blogPost: (id: string) => ['blog', id] as const,
  blogCategories: ['blog', 'categories'] as const,
  site: ['site'] as const,
  images: (category?: string) => ['images', category ?? 'all'] as const,
  contacts: (status?: string, cursor?: string) => ['contacts', status ?? 'all', cursor ?? 'first'] as const,
  contact: (id: string) => ['contacts', id] as const,
  quotes: ['quotes'] as const,
  catalogAdminAreas: ['catalog-admin', 'service-areas'] as const,
  catalogAdminItems: ['catalog-admin', 'items'] as const,
};
